import { useOutletContext } from 'react-router-dom'

import {
  AutoGrid,
  DetailsItem,
  OneEmptyState,
  Spinner,
} from '@factorialco/f0-react/dist/experimental'
import { ItAssetDetailsQuery } from 'generated/resources/graphql'

import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ActivityLog from './ActivityLog'

const AssetDetailsOverview = () => {
  const context = useOutletContext<{
    asset: ItAssetDetailsQuery['itManagement']['itAsset']
    assetId: string
    refreshAsset: () => Promise<void>
  }>()

  if (!context) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="large" />
      </div>
    )
  }

  const { asset } = context

  if (!asset) {
    return (
      <div className="p-6">
        <OneEmptyState
          title={i18n.t('it_management.asset_details.asset_not_found')}
          description={i18n.t('it_management.asset_details.asset_not_found_description')}
          variant="critical"
        />
      </div>
    )
  }

  const Separator = () => (
    <div style={{ width: '100%', height: 1, borderBottom: '1px dashed rgba(5, 35, 72, 0.14)' }} />
  )

  return (
    <div className="flex">
      <div className="space-y-6 w-3/4 pr-4">
        <section className="p-6">
          <h2 className="text-lg font-semibold pb-4">
            {i18n.t('it_management.asset_details.basic_details')}
          </h2>
          <AutoGrid gap="4" tileSize="md">
            <DetailsItem
              content={{
                text: asset.itAssetModel?.name ?? '-',
                type: 'item',
              }}
              title={i18n.t('it_management.common.name')}
            />
            <DetailsItem
              content={{
                text: asset.serialNumber || '-',
                type: 'item',
              }}
              title={i18n.t('it_management.common.serial_number')}
            />
            <DetailsItem
              content={{
                text: asset.itAssetModel?.brand ?? '-',
                type: 'item',
              }}
              title={i18n.t('it_management.new_equipment_modal.fields.manufacturer')}
            />
            <DetailsItem
              content={{
                text: asset.label || '-',
                type: 'item',
              }}
              title={i18n.t('it_management.common.internal_label')}
            />
            <DetailsItem
              content={{
                text: asset.itAssetModel?.name ?? '-',
                type: 'item',
              }}
              title={i18n.t('it_management.new_equipment_modal.fields.model_name')}
            />
          </AutoGrid>
        </section>

        {asset.notes && asset.notes.trim() !== '' && (
          <>
            <Separator />
            <section className="p-6">
              <h2 className="text-lg font-semibold pb-4">{i18n.t('it_management.common.notes')}</h2>
              <p className="text-sm text-f1-foreground-secondary whitespace-pre-wrap py-1 px-1">
                {asset.notes}
              </p>
            </section>
          </>
        )}

        <Separator />

        <section className="p-6">
          <h2 className="text-lg font-semibold pb-4">
            {i18n.t('it_management.new_equipment_modal.sections.finance_information')}
          </h2>
          <AutoGrid gap="4" tileSize="md">
            <DetailsItem
              content={{
                text: asset.purchaseDate ? convertDate(asset.purchaseDate, 'backend', 'ui') : '-',
                type: 'item',
              }}
              title={i18n.t('it_management.new_equipment_modal.fields.purchase_date')}
            />

            <DetailsItem
              content={{
                text: asset.warrantyEndDate
                  ? convertDate(asset.warrantyEndDate, 'backend', 'ui')
                  : '-',
                type: 'item',
              }}
              title={i18n.t('it_management.new_equipment_modal.fields.warranty_date')}
            />

            <DetailsItem
              content={{
                text: (asset.purchasePriceCents ? asset.purchasePriceCents / 100 : '-') + '€',
                type: 'item',
              }}
              title={i18n.t('it_management.new_equipment_modal.fields.total_cost')}
            />
          </AutoGrid>
        </section>
      </div>

      <div className="w-1/4 pt-6 flex flex-col mt-6">
        <label className="text-sm font-semibold uppercase text-f1-foreground-secondary pl-4 mb-2">
          {i18n.t('it_management.asset_details.activity')}
        </label>
        <div className="flex-1 overflow-y-auto min-h-0">
          <ActivityLog assetId={asset.id} />
        </div>
      </div>
    </div>
  )
}

export default AssetDetailsOverview
